// Phase COTTON-RM-002 Part C — Cotton Cost Restatement workflow.
//
// Tabs: All · Pending Finance Review · Approved (awaiting stage) ·
// Posted · Rejected. Drawer shows bale-by-bale impact with
// approve / reject / stage-finance actions.

import { useMemo, useState } from 'react'
import {
  App, Badge, Button, Card, Col, Descriptions, Drawer, Empty, Form, Input,
  Modal, Row, Select, Skeleton, Space, Statistic, Table, Tabs, Tag,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined, CloseCircleOutlined, DollarOutlined, ReloadOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  cottonRmApiPartC, type CottonCostRestatement,
  type RestatementApprovalStatus, type RestatementImpactLine,
} from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

function statusColor(s: RestatementApprovalStatus) {
  if (s === 'APPROVED')                return 'green'
  if (s === 'PENDING_FINANCE_REVIEW')  return 'warning'
  if (s === 'REJECTED')                return 'red'
  if (s === 'POSTED')                  return 'blue'
  if (s === 'CANCELLED')               return 'default'
  return 'default'
}

const TAB_FILTERS: { key: string; label: string; status?: RestatementApprovalStatus }[] = [
  { key: 'all',      label: 'All' },
  { key: 'pending',  label: 'Pending Finance Review', status: 'PENDING_FINANCE_REVIEW' },
  { key: 'approved', label: 'Approved (awaiting stage)', status: 'APPROVED' },
  { key: 'posted',   label: 'Posted',                   status: 'POSTED' },
  { key: 'rejected', label: 'Rejected',                 status: 'REJECTED' },
]


function ImpactDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [remarks, setRemarks] = useState('')
  const [debit, setDebit] = useState('')
  const [credit, setCredit] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['restate-impact', id],
    queryFn: () => cottonRmApiPartC.restatementImpact(id!),
    enabled: !!id,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['restate-list'] })
    qc.invalidateQueries({ queryKey: ['restate-impact', id] })
  }

  const approveMut = useMutation({
    mutationFn: () => cottonRmApiPartC.approveRestatement(id!, remarks || undefined),
    onSuccess: () => {
      message.success('Restatement APPROVED. Stage to finance next.')
      setRemarks(''); invalidate()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const rejectMut = useMutation({
    mutationFn: () => {
      if (!remarks.trim()) throw new Error('Remarks are required for rejection.')
      return cottonRmApiPartC.rejectRestatement(id!, remarks)
    },
    onSuccess: () => {
      message.success('Restatement REJECTED.'); setRemarks(''); invalidate()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const stageMut = useMutation({
    mutationFn: () => cottonRmApiPartC.stageRestatementFinance(id!,
      { debit_ledger: debit || undefined, credit_ledger: credit || undefined }),
    onSuccess: () => {
      message.success('Finance posting STAGED.'); invalidate()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const lineCols = [
    { title: 'Bale', dataIndex: 'bale_no' },
    { title: 'Status', dataIndex: 'status',
      render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Wt (kg)', dataIndex: 'weight_kg', align: 'right' as const,
      render: (n: number) => n.toFixed(1) },
    { title: 'Old ₹/kg', dataIndex: 'old_per_kg', align: 'right' as const,
      render: (n: number) => `₹${n.toFixed(2)}` },
    { title: 'New ₹/kg', dataIndex: 'new_per_kg', align: 'right' as const,
      render: (n: number) => `₹${n.toFixed(2)}` },
    { title: 'Old total', dataIndex: 'old_total', align: 'right' as const,
      render: (n: number) => `₹${n.toFixed(2)}` },
    { title: 'New total', dataIndex: 'new_total', align: 'right' as const,
      render: (n: number) => `₹${n.toFixed(2)}` },
    { title: 'Δ', dataIndex: 'difference', align: 'right' as const,
      render: (n: number) => (
        <Text style={{ color: n < 0 ? '#ff4d4f' : '#52c41a' }}>
          {n < 0 ? '−' : '+'}₹{Math.abs(n).toFixed(2)}
        </Text>
      ) },
  ]

  const canApprove = data?.approval_status === 'PENDING_FINANCE_REVIEW' || data?.approval_status === 'DRAFT'
  const canStage = data?.approval_status === 'APPROVED'

  return (
    <Drawer open={!!id} onClose={onClose} title="Restatement Impact" width={900}>
      {isLoading || !data ? <Skeleton active /> : (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col span={6}><Card size="small">
              <Statistic title="Old total" value={data.old_total_cost} prefix="₹" precision={2} />
            </Card></Col>
            <Col span={6}><Card size="small">
              <Statistic title="New total" value={data.new_total_cost} prefix="₹" precision={2} />
            </Card></Col>
            <Col span={6}><Card size="small">
              <Statistic title="Difference" value={data.difference_amount}
                prefix={data.difference_amount < 0 ? '−₹' : '+₹'}
                precision={2}
                valueStyle={{ color: data.difference_amount < 0 ? '#ff4d4f' : '#52c41a' }} />
            </Card></Col>
            <Col span={6}><Card size="small">
              <Statistic title="Bales affected" value={data.affected_bale_count} />
            </Card></Col>
          </Row>

          <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Status">
              <Tag color={statusColor(data.approval_status)}>{data.approval_status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Finance posting">
              <Tag>{data.finance_posting_status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Adjustment">
              {data.adjustment_type} · {data.adjustment_sign === -1 ? '−' : '+'}₹{data.adjustment_amount?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Affected">
              {data.affected_issue_slip_count} slips · {data.affected_laydown_count} laydowns · {data.affected_fg_lot_count} FG lots
            </Descriptions.Item>
            <Descriptions.Item label="Debit ledger">{data.debit_ledger ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Credit ledger">{data.credit_ledger ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Reason" span={2}>{data.reason}</Descriptions.Item>
            {data.remarks && (
              <Descriptions.Item label="Review remarks" span={2}>{data.remarks}</Descriptions.Item>
            )}
          </Descriptions>

          <Title level={5}>Per-bale impact</Title>
          <Table rowKey="bale_id" size="small" pagination={false}
            dataSource={data.lines as RestatementImpactLine[]}
            columns={lineCols as never} />

          {(canApprove || canStage) && (
            <Card size="small" style={{ marginTop: 12 }}
                  title="Actions">
              {canApprove && (
                <>
                  <Form layout="vertical">
                    <Form.Item label="Reviewer remarks (required for reject)">
                      <Input.TextArea rows={2} value={remarks}
                        onChange={e => setRemarks(e.target.value)} />
                    </Form.Item>
                    <Space>
                      <Button type="primary" icon={<CheckCircleOutlined />}
                              loading={approveMut.isPending}
                              onClick={() => approveMut.mutate()}>
                        Approve
                      </Button>
                      <Button danger icon={<CloseCircleOutlined />}
                              loading={rejectMut.isPending}
                              onClick={() => rejectMut.mutate()}>
                        Reject
                      </Button>
                    </Space>
                  </Form>
                </>
              )}
              {canStage && (
                <>
                  <Form layout="vertical">
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item label="Debit ledger (override)">
                          <Input value={debit} placeholder={data.debit_ledger ?? ''}
                            onChange={e => setDebit(e.target.value)} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Credit ledger (override)">
                          <Input value={credit} placeholder={data.credit_ledger ?? ''}
                            onChange={e => setCredit(e.target.value)} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button type="primary" icon={<DollarOutlined />}
                            loading={stageMut.isPending}
                            onClick={() => stageMut.mutate()}>
                      Stage to finance (POSTED)
                    </Button>
                  </Form>
                </>
              )}
            </Card>
          )}
        </>
      )}
    </Drawer>
  )
}


function CreateFromAdjModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [adjId, setAdjId] = useState<string | undefined>()
  const [reason, setReason] = useState('')

  const { data: warnedAdj = [] } = useQuery({
    queryKey: ['adj-with-warning'],
    queryFn: () => cottonRmApiPartC.costAdjReport({
      approval_status: 'APPROVED', downstream_only: true, limit: 200,
    }),
    enabled: open,
  })

  const createMut = useMutation({
    mutationFn: () => {
      if (!adjId)         throw new Error('Pick an adjustment.')
      if (!reason.trim()) throw new Error('Reason is required.')
      return cottonRmApiPartC.createRestatement(adjId, reason)
    },
    onSuccess: () => {
      message.success('Restatement request created.')
      qc.invalidateQueries({ queryKey: ['restate-list'] })
      setAdjId(undefined); setReason(''); onClose()
    },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <Modal open={open} title="New Restatement Request"
            onCancel={onClose} onOk={() => createMut.mutate()}
            confirmLoading={createMut.isPending} width={680}>
      <Form layout="vertical">
        <Form.Item label="Approved adjustment with downstream warning"
                   required>
          <Select placeholder="Pick adjustment"
                  value={adjId} onChange={setAdjId}
                  showSearch optionFilterProp="label"
                  options={warnedAdj.map(a => ({
                    value: a.id,
                    label: `${a.grn_no} · ${a.adjustment_type} · ${a.sign < 0 ? '−' : '+'}₹${a.amount.toLocaleString()}`,
                  }))} />
        </Form.Item>
        <Form.Item label="Reason for restatement" required>
          <Input.TextArea rows={3} value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Why is this restatement being raised?" />
        </Form.Item>
      </Form>
    </Modal>
  )
}


export default function CottonCostRestatementsPage() {
  const [tab, setTab] = useState('all')
  const [drawerId, setDrawerId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const filter = TAB_FILTERS.find(t => t.key === tab)
  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['restate-list', filter?.status ?? 'all'],
    queryFn: () => cottonRmApiPartC.listRestatements({
      approval_status: filter?.status, limit: 500,
    }),
  })

  const allRows = useQuery({
    queryKey: ['restate-counts'],
    queryFn: () => cottonRmApiPartC.listRestatements({ limit: 1000 }),
  })

  const counts = useMemo(() => {
    const data = allRows.data ?? []
    const c: Record<string, number> = { all: data.length }
    for (const r of data) c[r.approval_status] = (c[r.approval_status] ?? 0) + 1
    return c
  }, [allRows.data])

  const cols = [
    { title: 'Requested', dataIndex: 'requested_at',
      render: (v: string) => new Date(v).toLocaleString() },
    { title: 'Bales', dataIndex: 'affected_bale_count' },
    { title: 'Old total', dataIndex: 'old_total_cost',
      align: 'right' as const,
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'New total', dataIndex: 'new_total_cost',
      align: 'right' as const,
      render: (n: number) => `₹${n.toLocaleString()}` },
    { title: 'Δ', dataIndex: 'difference_amount',
      align: 'right' as const,
      render: (n: number) => (
        <Text style={{ color: n < 0 ? '#ff4d4f' : '#52c41a' }}>
          {n < 0 ? '−' : '+'}₹{Math.abs(n).toLocaleString()}
        </Text>
      ) },
    { title: 'Status', dataIndex: 'approval_status',
      render: (s: RestatementApprovalStatus) =>
        <Tag color={statusColor(s)}>{s}</Tag> },
    { title: 'Finance', dataIndex: 'finance_posting_status',
      render: (s: string) => <Tag>{s}</Tag> },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: '', key: 'open',
      render: (_: unknown, r: CottonCostRestatement) =>
        <Button size="small" onClick={() => setDrawerId(r.id)}>Open</Button> },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1600, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Cotton Cost Restatements</Title>
      <Text type="secondary">
        Historical-cost restatement workflow for issued/consumed bales.
        Approval &gt; Stage to finance are <b>two separate steps</b>: a
        rejected request does NOT stage to finance.
      </Text>

      <Space style={{ marginTop: 12, marginBottom: 8 }}>
        <Button type="primary" icon={<DollarOutlined />}
                onClick={() => setCreateOpen(true)}>
          New from approved adjustment
        </Button>
        <Button icon={<ReloadOutlined spin={isFetching} />}
                onClick={() => refetch()}>Refresh</Button>
      </Space>

      <Card>
        <Tabs activeKey={tab} onChange={setTab}
              items={TAB_FILTERS.map(t => ({
                key: t.key,
                label: (
                  <Space size={4}>
                    <span>{t.label}</span>
                    <Badge count={t.status ? (counts[t.status] ?? 0) : counts.all}
                            showZero={false}
                            style={{ backgroundColor:
                              t.key === 'rejected' ? '#ff4d4f' :
                              t.key === 'posted' ? '#1677ff' : '#faad14' }} />
                  </Space>
                ),
                children: isLoading ? <Skeleton active /> :
                  rows.length === 0 ? <Empty /> : (
                    <Table rowKey="id" size="small" columns={cols as never}
                            dataSource={rows} pagination={{ pageSize: 30 }} />
                  ),
              }))} />
      </Card>

      <ImpactDrawer id={drawerId} onClose={() => setDrawerId(null)} />
      <CreateFromAdjModal open={createOpen}
                          onClose={() => setCreateOpen(false)} />
    </div>
  )
}
